import { normalizeForSearch, tokenize } from "./normalize";

/** The fields ranking reads; Admin's `SearchDoc` and the backend's index document both have them. */
export interface MatchableDoc {
  id: string;
  businessName: string;
  ownerNames: string[];
  emails: string[];
  haystack: string;
}

export interface MatchHit<T extends MatchableDoc> {
  doc: T;
  score: number;
}

/**
 * Relative importance of each field a query can match. An admin typing a name
 * is far more often naming a business than its owner, and an email is usually
 * pasted whole rather than guessed at, so a partial email match is the weakest
 * signal of the three.
 */
const FIELD_WEIGHT = {
  businessName: 100,
  ownerName: 20,
  email: 15,
} as const;

/**
 * Beats every text score, so a pasted id always leads.
 *
 * Ids are matched exactly rather than folded into the haystack: a UUID
 * tokenizes into short hex runs, which would prefix-match ordinary queries and
 * surface unrelated merchants at a zero text score.
 */
const ID_EXACT_SCORE = 10_000;

/** The shortest input worth testing as an id, to skip the check while typing. */
const MIN_ID_LENGTH = 8;

/** How well a query sits against one field, independent of which field it is. */
const enum MatchTier {
  None = 0,
  /** Every query token prefixes some token in the field, in any order. */
  Token = 1,
  /** The field text itself begins with the query. */
  FieldPrefix = 2,
  /** The field text equals the query. */
  Exact = 3,
}

function tierFor(
  field: string,
  query: string,
  queryTokens: string[],
): MatchTier {
  const normalized = normalizeForSearch(field);
  if (normalized === "") return MatchTier.None;
  if (normalized === query) return MatchTier.Exact;
  if (normalized.startsWith(query)) return MatchTier.FieldPrefix;

  const fieldTokens = normalized.split(" ");
  const everyTokenMatches = queryTokens.every((queryToken) =>
    fieldTokens.some((fieldToken) => fieldToken.startsWith(queryToken)),
  );
  return everyTokenMatches ? MatchTier.Token : MatchTier.None;
}

/**
 * Best weighted tier across every field, rather than a sum.
 *
 * Summing would let a merchant whose owner name and email both weakly contain
 * the query outrank one whose business name matches it outright, which is
 * never what the admin meant.
 */
function scoreDoc(
  doc: MatchableDoc,
  query: string,
  queryTokens: string[],
): number {
  const candidates = [
    FIELD_WEIGHT.businessName * tierFor(doc.businessName, query, queryTokens),
    ...doc.ownerNames.map(
      (name) => FIELD_WEIGHT.ownerName * tierFor(name, query, queryTokens),
    ),
    ...doc.emails.map(
      (email) => FIELD_WEIGHT.email * tierFor(email, query, queryTokens),
    ),
  ];
  return Math.max(0, ...candidates);
}

/**
 * True when every query token prefixes some token in the document's
 * build-time `haystack`.
 *
 * This is the cheap gate: it runs over the whole base on each keystroke, so it
 * touches one pre-normalized string per document and never re-normalizes.
 * Only the survivors are scored field by field.
 */
function passesPrefilter(doc: MatchableDoc, queryTokens: string[]): boolean {
  const haystackTokens = doc.haystack.split(" ");
  return queryTokens.every((queryToken) =>
    haystackTokens.some((token) => token.startsWith(queryToken)),
  );
}

/**
 * Ranked matches for `rawQuery` across `docs`.
 *
 * All query tokens must match (AND), so adding a word always narrows the
 * result set. A blank query returns every document, which is what makes the
 * merchants list and the search box a single code path — but a query that is
 * only punctuation is not blank: it matches nothing, rather than everything.
 *
 * Ties break on business name then id so repeated identical queries always
 * produce the same order, and pagination over the result stays stable.
 */
export function searchDocs<T extends MatchableDoc>(
  docs: T[],
  rawQuery: string,
): MatchHit<T>[] {
  const query = normalizeForSearch(rawQuery);
  const queryTokens = tokenize(rawQuery);

  // Typed-but-unsearchable input (punctuation only) must not read as blank.
  if (queryTokens.length === 0 && rawQuery.trim() !== "") return [];

  const idCandidate = rawQuery.trim().toLowerCase();
  const couldBeId =
    idCandidate.length >= MIN_ID_LENGTH && !idCandidate.includes(" ");

  const hits: MatchHit<T>[] =
    queryTokens.length === 0
      ? docs.map((doc) => ({ doc, score: 0 }))
      : docs
          .map((doc) => {
            if (couldBeId && doc.id.toLowerCase() === idCandidate) {
              return { doc, score: ID_EXACT_SCORE };
            }
            if (!passesPrefilter(doc, queryTokens)) return null;
            return { doc, score: scoreDoc(doc, query, queryTokens) };
          })
          .filter((hit): hit is MatchHit<T> => hit !== null);

  return hits.sort(
    (a, b) =>
      b.score - a.score ||
      a.doc.businessName.localeCompare(b.doc.businessName) ||
      a.doc.id.localeCompare(b.doc.id),
  );
}
